/**
 * @render react
 * @name Hero
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Heading } from 'Components/molecules/Heading';
import { PrimaryMedia } from 'Components/molecules/PrimaryMedia';

import './_style.scss';

/**
 * Hero component
 * @param {string} title - Title of the component
 * @param {string} subtitle - Subtitle of the component
 * @param {string} mediaType - mediaType for PrimaryMedia component
 * @param {string} src - src for PrimaryMedia component
 * @param {string} alt - alt for PrimaryMedia component
 * @param {string} linkType - linkType for PrimaryMedia component
 * @param {bool} mediaLazyLoad - mediaLazyLoad for PrimaryMedia component
 * @param {string} mediaSize - mediaSize for PrimaryMedia component
 * @param {node} children - component children
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */
const Hero = (
  {
    adBlock,
    showPrimaryMedia,
    title,
    subtitle,
    mediaType,
    src,
    alt,
    linkType,
    mediaLazyLoad,
    mediaSize,
    children,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
    images,
    renderPictureTag,
  },
) => (
  <div
    className={classNames({
      [`${cssPrefix}__hero`]: loadStyles,
      ...extraClassNames,
    })}
  >
    <div className={`${cssPrefix}__hero--inner`}>
      {mediaType && showPrimaryMedia && (
        <PrimaryMedia
          mediaType={mediaType}
          src={src}
          alt={alt}
          images={images}
          linkType={linkType}
          mediaLazyLoad={mediaLazyLoad}
          mediaSize={mediaSize}
          extraClassNames={{ [`${cssPrefix}__hero--media`]: true }}
          renderPictureTag={renderPictureTag}
        />
      )}

      {adBlock}

      {(title
        || subtitle
        || (
          (Array.isArray(children) && children.length > 0)
          || (typeof children === 'object' && Object.keys(children).length > 0)
        )
      ) && (
        <div className={`${cssPrefix}__hero--wrapper`}>
          <div className={`${cssPrefix}__hero--content`}>
            {title && (
              <Heading size={1} extraClassNames={{ [`${cssPrefix}__hero--title`]: true }}>
                <span>
                  {title}
                  {' '}
                </span>
                {subtitle && (
                  <span className={`${cssPrefix}__hero--subtitle`}>{subtitle}</span>
                )}
              </Heading>
            )}

            {children && (
              <div className={`${cssPrefix}__hero--children`}>
                {children}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);

/**
 * Valid props
 */
Hero.propTypes = {
  showPrimaryMedia: PropTypes.bool,
  adBlock: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  mediaType: PropTypes.string,
  src: PropTypes.string,
  alt: PropTypes.string,
  linkType: PropTypes.string,
  mediaLazyLoad: PropTypes.bool,
  mediaSize: PropTypes.string,
  children: PropTypes.node,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  images: PropTypes.arrayOf(PropTypes.shape()),
  renderPictureTag: PropTypes.bool,
};

/**
 * Default props
 */
Hero.defaultProps = {
  showPrimaryMedia: true,
  adBlock: [],
  title: '',
  subtitle: '',
  mediaType: 'Image',
  src: '',
  alt: '',
  linkType: '',
  mediaLazyLoad: false,
  mediaSize: 'content-image',
  children: [],
  loadStyles: true,
  extraClassNames: {},
  context: {},
  images: [],
  renderPictureTag: false,
};

export default withPolaris(Hero);
